import { FC, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Text,
  GreyGrey0,
  GreyGrey30,
  GreyGrey70,
  GreyWhite,
  Primary13Regular,
  Skeleton,
  Spinner,
  StandardLayout,
  Center,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { automationsPaths } from 'Automations/router'

const InboxPreviewComponent: FC<{
  header: ReactNode
  children: ReactNode
  'data-testid'?: string
}> = ({
  header,
  children,
  'data-testid':
    dataTestId = 'automations.management.executionsHistory.executionSummary'
}) => {
  const navigate = useNavigate()

  return (
    <Grid
      data-testid={dataTestId}
      height="100%"
      width="100%"
      minWidth="720px"
      overflow="hidden"
      gridTemplateRows="auto 1fr"
    >
      <Grid
        height="48px"
        padding="0 16px"
        width="100%"
        overflow="hidden"
        gridTemplateColumns={'1fr auto'}
        columnGap="8px"
        alignItems="center"
        backgroundColor={GreyWhite}
        borderBottom={`1px solid ${GreyGrey30}`}
      >
        <Box minWidth={0} overflow="hidden">
          {header}
        </Box>
        <StandardLayout.ButtonClose
          onClick={() => navigate(automationsPaths.automationsList())}
          data-testid="automations.management.executionsHistory.close"
        />
      </Grid>
      <Box
        position="relative"
        height="100%"
        width="100%"
        overflowY="auto"
        overflowX="hidden"
        background={GreyWhite}
      >
        {children}
      </Box>
    </Grid>
  )
}

const EmptyState: FC<{ children?: ReactNode }> = ({ children }) => (
  <InboxPreviewComponent
    header={null}
    data-testid="automations.management.executionsHistory.emptyPreview"
  >
    <Center height="100%" width="100%" background={GreyGrey0}>
      {children && (
        <Text {...Primary13Regular} color={GreyGrey70}>
          {children}
        </Text>
      )}
    </Center>
  </InboxPreviewComponent>
)

// Mirrors the structure of a loaded preview (header + content) so the layout
// doesn't jump when data arrives. The header shows skeleton placeholders for
// the task icon and title; the content keeps the centred spinner.
const LoadingState: FC = () => {
  return (
    <InboxPreviewComponent
      data-testid="automations.management.executionsHistory.preview.loading"
      header={
        <Flex alignItems="center" gap="8px">
          <Skeleton height="32px" width="32px" />
          <Skeleton height="16px" width="160px" />
        </Flex>
      }
    >
      <Center height="100%" width="100%">
        <Spinner />
      </Center>
    </InboxPreviewComponent>
  )
}

export const InboxPreview = Object.assign(InboxPreviewComponent, {
  EmptyState,
  LoadingState
})
